// Client-side image downscale + base64 encode. Keeps uploads small and avoids
// CPU-heavy base64 work on the server.

const MAX_EDGE = 2048;
const QUALITY = 0.92;

export async function downscaleToBase64(file: Blob): Promise<string> {
  const dataUrl = await readAsDataUrl(file);
  const img = await loadImage(dataUrl);

  let { width, height } = img;
  if (Math.max(width, height) > MAX_EDGE) {
    const scale = MAX_EDGE / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl; // fallback: original
  // High-quality smoothing makes shrinking a large phone photo much crisper.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', QUALITY);
}

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Crop a garment out of a photo using a bounding box normalized to a 1000x1000
// image (as returned by /api/ai/detect-garments), with a little padding so the
// cutout model sees the whole item. Replaces the reference project's
// server-side `sharp` crop — Cloudflare Workers can't run sharp, and the
// browser already has the full-resolution pixels. Returns a JPEG data URL.
export async function cropToBase64(
  dataUrl: string,
  box: BoundingBox,
  paddingPct = 0.08,
): Promise<string> {
  const img = await loadImage(dataUrl);
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;

  const rawLeft = (box.x / 1000) * iw;
  const rawTop = (box.y / 1000) * ih;
  const rawWidth = (box.width / 1000) * iw;
  const rawHeight = (box.height / 1000) * ih;
  const pad = Math.max(12, Math.round(Math.max(rawWidth, rawHeight) * paddingPct));

  const left = Math.max(0, Math.floor(rawLeft - pad));
  const top = Math.max(0, Math.floor(rawTop - pad));
  const right = Math.min(iw, Math.ceil(rawLeft + rawWidth + pad));
  const bottom = Math.min(ih, Math.ceil(rawTop + rawHeight + pad));
  const width = Math.max(1, right - left);
  const height = Math.max(1, bottom - top);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl; // fallback: original
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, left, top, width, height, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', QUALITY);
}

export function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Capture a remote image to a base64 JPEG entirely in the browser. Works when
// the image host sends permissive CORS headers (e.g. cdn.shopify.com and most
// retailer image CDNs). Returns null if the image can't be read (tainted
// canvas / load failure / timeout) so callers can fall back.
export function remoteImageToBase64(url: string, timeoutMs = 12000): Promise<string | null> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (v: string | null) => {
      if (settled) return;
      settled = true;
      resolve(v);
    };

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.referrerPolicy = 'no-referrer';
    img.decoding = 'async';

    const timer = setTimeout(() => finish(null), timeoutMs);

    img.onload = () => {
      clearTimeout(timer);
      try {
        let w = img.naturalWidth || img.width;
        let h = img.naturalHeight || img.height;
        if (!w || !h) return finish(null);
        if (Math.max(w, h) > MAX_EDGE) {
          const scale = MAX_EDGE / Math.max(w, h);
          w = Math.round(w * scale);
          h = Math.round(h * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return finish(null);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, w, h);
        // Throws (SecurityError) if the canvas is tainted by a non-CORS image.
        finish(canvas.toDataURL('image/jpeg', QUALITY));
      } catch {
        finish(null);
      }
    };
    img.onerror = () => {
      clearTimeout(timer);
      finish(null);
    };

    img.src = url;
  });
}
